import { BarDatum, BarChartComponentData } from '../../../../@types';
import { sanitizeKey } from '../../../../utils/dataUtils';
import { ReferenceMap } from '../../utils/reference';
import {
  AggregateObj,
  firstPassAggregateFunction,
  secondPassAggregateFunction,
} from '../../utils/aggregation';

/**
 * Returns a list of all keys in the mapped object to use in the chart
 *
 * Note: For bar charts, the keys are ALWAYS in the second group
 * @param groupBy
 * @param reference
 * @returns
 */
export const getChartKeys = (
  groupBy: BarChartComponentData['groupBy'],
  reference: ReferenceMap,
): Array<string> => {
  // For single dataset barcharts, default keys to first groupBy key
  if (groupBy.length < 2) return [sanitizeKey(groupBy[0].key)];

  // For grouped bar charts, the keys are ALWAYS in the second group
  const [, keyGroup] = groupBy;
  if (keyGroup.reference) {
    const refs = reference[keyGroup.reference.id];
    return refs ? refs.map((ref) => sanitizeKey(ref.label)) : [];
  }
  return [sanitizeKey(keyGroup.key)];
};

/**
 * Calculates the percentages in the mapped value
 */
export const calculatePercentages = (
  aggrKey: BarChartComponentData['aggrKey'],
  data: Array<BarDatum>,
  keys: string[],
): Array<BarDatum> => {
  // For single keys, the percentage is calculated across all buckets
  let total = 0;
  if (keys.length === 1) {
    total = data.reduce((acc, d) => {
      if (Number.isNaN(d[keys[0]])) return acc;
      return acc + Number(d[keys[0]]);
    }, 0);
  }

  return data.map((d) => {
    // For multiple keys, the percentage is calculated within each bucket
    if (keys.length > 1) {
      total = secondPassAggregateFunction(
        Object.entries(d).reduce((aggr, [k, value]) => {
          if (k === sanitizeKey(aggrKey.key)) return aggr;
          const aggregator = firstPassAggregateFunction(
            { key: '{{value}}', operation: 'sum' },
            { value },
          );
          return aggregator(aggr);
        }, {} as AggregateObj),
      );
    }

    // Object.fromEntries() does not work well with keys like `key0, key1`
    return Object.fromEntries(
      Object.entries(d).flatMap(([k, value]) => {
        if (k === sanitizeKey(aggrKey.key)) return [[k, value]];
        if (Number.isNaN(value)) return [[k, value]];
        return [
          [`${k}%`, (value as number) / (total || 1)],
          [k, value],
        ];
      }),
    );
  });
};
